import React, { useState, useEffect, useMemo } from "react";
import { Icon } from "../../icons.jsx";
import {
  Card, Button, Badge, Field, Switch, Modal, ReviewBadge,
} from "../components.jsx";
import { useStore } from "../store.jsx";
import { go } from "../rutas.js";
import { buildTree, flattenTree, validSupervisors, promoteTarget, demoteTarget } from "../hierarchy.js";

import { PageHead } from "./dashboard.jsx";
import { KPIMini, ValuationStatus } from "./orgchart.jsx";

/* ============================================================
   Talention — Organigrama (árbol de puestos)
   Jerarquía real puesto a puesto a través de reportsToId.
   ============================================================ */

function OrgChart() {
  const { state, dispatch } = useStore();
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [showHeadcount, setShowHeadcount] = useState(false);   // dato opcional: apagado por defecto
  const [deptFilter, setDeptFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [supervisorFor, setSupervisorFor] = useState(null);

  const roots = useMemo(() => buildTree(state.positions), [state.positions]);
  const nodes = useMemo(() => flattenTree(roots), [roots]);

  const maxDepth = nodes.reduce((deepest, n) => Math.max(deepest, n.depth + 1), 0);
  const parentIds = nodes.filter(n => n.children.length > 0).map(n => n.position.id);
  const toReview = state.positions.filter(p => p.review);

  const filterDeptId = deptFilter === "all" ? null : parseInt(deptFilter, 10);
  const matchesFilter = (p) => filterDeptId == null || p.departmentId === filterDeptId;
  const matching = state.positions.filter(matchesFilter).length;

  const toggleCollapse = (positionId) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(positionId)) next.delete(positionId);
      else next.add(positionId);
      return next;
    });
  };

  // Mover un puesto bajo un superior plegado lo escondería dentro de una rama
  // cerrada: se despliega el destino para que el puesto siga a la vista.
  const expand = (positionId) => {
    if (positionId == null) return;
    setCollapsed(prev => {
      if (!prev.has(positionId)) return prev;
      const next = new Set(prev);
      next.delete(positionId);
      return next;
    });
  };

  return (
    <div className="stack" style={{ gap: 22 }}>
      <PageHead
        title="Organigrama"
        sub="Jerarquía real puesto a puesto: de quién depende cada puesto. No confundir con el «Nivel jerárquico», que solo clasifica los puestos en categorías genéricas."
        actions={
          <div className="row">
            <Button icon="chevron-down" onClick={() => setCollapsed(new Set())}>Desplegar todo</Button>
            <Button icon="chevron-right" onClick={() => setCollapsed(new Set(parentIds))}>Plegar todo</Button>
          </div>
        }
      />

      <div className="grid grid-4">
        <KPIMini label="Puestos en el árbol"   value={state.positions.length} icon="briefcase" color="#0a84ff" />
        <KPIMini label="Puestos raíz"          value={roots.length}           icon="sitemap"   color="#7c3aed" />
        <KPIMini label="Profundidad máxima"    value={maxDepth}               icon="tree"      color="#0891b2" />
        <KPIMini label="Pendientes de revisión" value={toReview.length}       icon="alert"     color="#ff9f0a" />
      </div>

      {toReview.length > 0 && (
        <div className="banner warn">
          <Icon name="alert" size={18} className="banner-icon" />
          <div style={{ flex: 1 }}>
            <div className="semi" style={{ fontSize: 13 }}>
              {toReview.length === 1
                ? "1 puesto está pendiente de revisión"
                : `${toReview.length} puestos están pendientes de revisión`}
            </div>
            <div className="tiny" style={{ marginTop: 2, opacity: 0.85 }}>
              Un puesto subordinado no debería puntuar más que el puesto del que depende.
              Revisa su valoración o cámbialo de sitio en el organigrama: el aviso desaparece solo.
            </div>
            <div className="stack" style={{ gap: 5, marginTop: 10 }}>
              {toReview.map(p => (
                <div key={p.id} className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  <b style={{ fontSize: 12.5 }}>{p.name}</b>
                  <span className="tiny" style={{ opacity: 0.85 }}>{p.review.message}</span>
                  <button
                    onClick={() => go("/valuation/" + p.id)}
                    style={{
                      border: "none", background: "white", padding: "3px 10px",
                      borderRadius: 999, fontSize: 11.5, fontWeight: 600, cursor: "pointer", color: "inherit",
                    }}
                  >
                    Revisar valoración
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Card className="flush">
        <div style={{
          padding: "13px 20px", background: "var(--bg-soft)", borderBottom: "1px solid var(--border)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "var(--ink-900)", color: "white", display: "grid", placeItems: "center" }}>
              <Icon name="sitemap" size={14} />
            </div>
            <span style={{ fontWeight: 650, fontSize: 13.5, letterSpacing: "-0.015em" }}>Árbol de dependencias</span>
          </div>
          <span style={{ fontSize: 11.5, color: "var(--ink-400)" }}>
            {roots.length} {roots.length === 1 ? "puesto raíz" : "puestos raíz"} · {maxDepth} {maxDepth === 1 ? "nivel" : "niveles"}
          </span>
        </div>

        <div style={{ padding: "14px 20px", display: "flex", gap: 12, alignItems: "center", borderBottom: "1px solid var(--border)", flexWrap: "wrap" }}>
          <select className="select" value={deptFilter} onChange={e => setDeptFilter(e.target.value)} style={{ width: "auto" }}>
            <option value="all">Todos los departamentos</option>
            {state.departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <Badge>{matching} de {state.positions.length} puestos</Badge>
          {filterDeptId != null && (
            <span className="tiny muted">Los puestos de otros departamentos se atenúan, no se ocultan: sacarlos rompería la jerarquía.</span>
          )}
          <div style={{ flex: 1 }} />
          <div className="row" style={{ gap: 8 }}>
            <Switch on={showHeadcount} onChange={setShowHeadcount} />
            <span className="tiny muted">Mostrar nº de personas</span>
          </div>
        </div>

        <div style={{ padding: "18px 20px" }}>
          {roots.length === 0 ? (
            <div className="empty">
              Todavía no hay puestos con los que montar el organigrama
              <div style={{ marginTop: 12 }}>
                <Button variant="secondary" icon="plus" onClick={() => go("/positions")}>Crear puestos</Button>
              </div>
            </div>
          ) : (
            roots.map(node => (
              <OrgNode
                key={node.position.id}
                node={node}
                state={state}
                dispatch={dispatch}
                collapsed={collapsed}
                onToggleCollapse={toggleCollapse}
                onExpand={expand}
                showHeadcount={showHeadcount}
                matchesFilter={matchesFilter}
                selectedId={selectedId}
                onSelect={setSelectedId}
                onChangeSupervisor={setSupervisorFor}
              />
            ))
          )}
        </div>
      </Card>

      <SupervisorModal
        open={supervisorFor !== null}
        position={supervisorFor}
        positions={state.positions}
        departments={state.departments}
        onClose={() => setSupervisorFor(null)}
        onSave={(reportsToId) => {
          dispatch({ type: "SET_POSITION_SUPERVISOR", positionId: supervisorFor.id, reportsToId });
          expand(reportsToId);
          setSupervisorFor(null);
        }}
      />
    </div>
  );
}

/* Una tarjeta por puesto y, colgando de ella, las de sus subordinados. */
function OrgNode({ node, state, dispatch, collapsed, onToggleCollapse, onExpand, showHeadcount, matchesFilter, selectedId, onSelect, onChangeSupervisor }) {
  const p = node.position;
  const dept = state.departments.find(d => d.id === p.departmentId);
  const hasChildren = node.children.length > 0;
  const isCollapsed = collapsed.has(p.id);
  const dimmed = !matchesFilter(p);

  const promote = promoteTarget(p.id, state.positions);
  const demote = demoteTarget(p.id, state.positions);

  // Las acciones solo asoman al pasar por encima; sin ratón no habría forma de
  // llegar a ellas, así que tocar la tarjeta la deja seleccionada y las muestra.
  const classes = "org-card"
    + (p.review ? " review" : "")
    + (selectedId === p.id ? " selected" : "");

  return (
    <div className="org-node">
      <div className={classes} onClick={() => onSelect(p.id)} style={{ opacity: dimmed ? 0.4 : 1, cursor: "pointer" }}>
        <button
          className={"org-toggle" + (hasChildren ? "" : " leaf")}
          onClick={() => onToggleCollapse(p.id)}
          title={isCollapsed ? "Desplegar subordinados" : "Plegar subordinados"}
        >
          <Icon name={isCollapsed ? "chevron-right" : "chevron-down"} size={14} />
        </button>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <span className="semi" style={{ fontSize: 13.5, letterSpacing: "-0.01em" }}>{p.name}</span>
            {hasChildren && isCollapsed && (
              <Badge>{node.children.length} {node.children.length === 1 ? "subordinado" : "subordinados"}</Badge>
            )}
            {p.review && <ReviewBadge review={p.review} compact />}
          </div>
          <div className="tiny muted" style={{ marginTop: 2 }}>{dept?.name || "Sin departamento"}</div>
        </div>

        {showHeadcount && (
          <span className="badge" title="Personas que ocupan el puesto">
            <Icon name="users" size={11} />
            {p.headcount == null ? "—" : p.headcount}
          </span>
        )}

        <ValuationStatus position={p} state={state} />

        <div className="org-actions">
          <ActionButton
            icon="arrow-up"
            title={promote.ok ? "Subir en la jerarquía" : promote.reason}
            disabled={!promote.ok}
            onClick={() => dispatch({ type: "PROMOTE_POSITION", positionId: p.id })}
          />
          <ActionButton
            icon="arrow-down"
            title={demote.ok ? "Bajar en la jerarquía" : demote.reason}
            disabled={!demote.ok}
            onClick={() => {
              dispatch({ type: "DEMOTE_POSITION", positionId: p.id });
              onExpand(demote.reportsToId);
            }}
          />
          <ActionButton
            icon="swap"
            title="Cambiar superior"
            onClick={() => onChangeSupervisor(p)}
          />
          <ActionButton
            icon="check-list"
            title="Valorar puesto"
            onClick={() => go("/valuation/" + p.id)}
          />
        </div>
      </div>

      {hasChildren && !isCollapsed && (
        <div className="org-children">
          {node.children.map(child => (
            <OrgNode
              key={child.position.id}
              node={child}
              state={state}
              dispatch={dispatch}
              collapsed={collapsed}
              onToggleCollapse={onToggleCollapse}
              onExpand={onExpand}
              showHeadcount={showHeadcount}
              matchesFilter={matchesFilter}
              selectedId={selectedId}
              onSelect={onSelect}
              onChangeSupervisor={onChangeSupervisor}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* El title va en el envoltorio: un botón deshabilitado no dispara eventos de
   ratón, así que su propio tooltip no llegaría a verse. */
function ActionButton({ icon, title, disabled, onClick }) {
  return (
    <span title={title} style={{ display: "flex" }}>
      <button
        className="icon-btn"
        disabled={disabled}
        onClick={onClick}
        style={{ width: 28, height: 28, opacity: disabled ? 0.35 : 1, cursor: disabled ? "default" : "pointer" }}
      >
        <Icon name={icon} size={13} />
      </button>
    </span>
  );
}

/* El select solo ofrece superiores válidos: ni el propio puesto ni sus
   subordinados, que crearían un ciclo. */
function SupervisorModal({ open, position, positions, departments, onClose, onSave }) {
  const [value, setValue] = useState("");
  useEffect(() => {
    setValue(position?.reportsToId == null ? "" : String(position.reportsToId));
  }, [open, position]);

  if (!position) return null;

  const candidates = validSupervisors(position.id, positions);
  const deptName = (p) => departments.find(d => d.id === p.departmentId)?.name || "Sin departamento";

  return (
    <Modal open={open} onClose={onClose} title={`Superior de ${position.name}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" onClick={() => onSave(value === "" ? null : parseInt(value, 10))}>
            Guardar dependencia
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 14 }}>
        <Field
          label="Depende de"
          hint="No se ofrecen ni el propio puesto ni sus subordinados: la dependencia se volvería circular."
        >
          <select className="select" value={value} onChange={e => setValue(e.target.value)}>
            <option value="">— Sin superior (raíz) —</option>
            {candidates.map(p => (
              <option key={p.id} value={p.id}>{p.name} · {deptName(p)}</option>
            ))}
          </select>
        </Field>
        <div className="banner info">
          <Icon name="info" size={16} className="banner-icon" />
          <div className="tiny" style={{ flex: 1 }}>
            Mover un puesto no cambia su puntuación. Si queda por encima o por debajo de donde le
            corresponde por puntos, aparecerá marcado como pendiente de revisión.
          </div>
        </div>
      </div>
    </Modal>
  );
}

export { OrgChart };
